import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { FileText, Trash2, Search, Briefcase, Target, DollarSign, Building2, Eye } from "lucide-react";
import { format } from "date-fns";
import { DashboardLayout } from "@/components/DashboardLayout";

interface Document {
  id: string;
  title: string;
  kind: string;
  format: string;
  bytes: number;
  created_at: string;
  storage_path: string;
}

interface ResumeVersion {
  id: string;
  created_at: string;
  template: string;
  ats_score: number;
  resume_json: any;
}

interface Letter {
  id: string;
  created_at: string;
  job_title: string;
  company: string;
  letter_text: string;
  tone: string;
}

interface ATSScore {
  id: string;
  created_at: string;
  overall_score: number;
  formatting_score: number;
  keywords_score: number;
  content_score: number;
  suggestions: any;
  strengths: any;
  weaknesses: any;
}

interface InterviewSession {
  id: string;
  created_at: string;
  interview_type: string;
  job_title: string;
  company_name: string;
  status: string;
  overall_score: number;
  duration: number;
}

interface JobAnalysis {
  id: string;
  created_at: string;
  job_title: string;
  company: string;
  experience_level: string;
  salary_range: string;
  keywords: any;
  required_skills: any;
}

interface SalaryAnalysis {
  id: string;
  created_at: string;
  job_title: string;
  location: string;
  current_salary: number;
  market_data: any;
  years_experience: number;
}

interface SavedJob {
  id: string;
  saved_at: string;
  title: string;
  company: string;
  location: string;
  job_type: string;
  remote: string;
  url: string;
  scanned: boolean;
}

type HistoryItem = {
  id: string;
  created_at: string;
  type: 'document' | 'resume' | 'letter' | 'ats' | 'interview' | 'job_analysis' | 'salary' | 'saved_job';
  data: Document | ResumeVersion | Letter | ATSScore | InterviewSession | JobAnalysis | SalaryAnalysis | SavedJob;
};

export default function Documents() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    checkAuth();
    loadAllHistory();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/login');
    }
  };

  const loadAllHistory = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const allItems: HistoryItem[] = [];

      // Load documents
      const { data: docs } = await supabase
        .from('documents')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });
      if (docs) {
        allItems.push(...docs.map(d => ({ id: d.id, created_at: d.created_at, type: 'document' as const, data: d })));
      }

      // Load resume versions
      const { data: resumes } = await supabase
        .from('resume_versions')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });
      if (resumes) {
        allItems.push(...resumes.map(r => ({ id: r.id, created_at: r.created_at, type: 'resume' as const, data: r })));
      }

      // Load cover letters
      const { data: letters } = await supabase
        .from('letters')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });
      if (letters) {
        allItems.push(...letters.map(l => ({ id: l.id, created_at: l.created_at, type: 'letter' as const, data: l })));
      }

      // Load ATS scores
      const { data: atsScores } = await supabase
        .from('ats_scores')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });
      if (atsScores) {
        allItems.push(...atsScores.map(a => ({ id: a.id, created_at: a.created_at, type: 'ats' as const, data: a as any })));
      }

      // Load interview sessions
      const { data: interviews } = await supabase
        .from('interview_sessions')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });
      if (interviews) {
        allItems.push(...interviews.map(i => ({ id: i.id, created_at: i.created_at, type: 'interview' as const, data: i as any })));
      }

      // Load job analyses
      const { data: analyses } = await supabase
        .from('job_analyses')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });
      if (analyses) {
        allItems.push(...analyses.map(a => ({ id: a.id, created_at: a.created_at, type: 'job_analysis' as const, data: a as any })));
      }

      // Load salary analyses
      const { data: salaries } = await supabase
        .from('salary_analyses')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });
      if (salaries) {
        allItems.push(...salaries.map(s => ({ id: s.id, created_at: s.created_at, type: 'salary' as const, data: s as any })));
      }

      // Load saved jobs
      const { data: savedJobs } = await supabase
        .from('saved_jobs')
        .select('*')
        .eq('user_id', session.user.id)
        .order('saved_at', { ascending: false });
      if (savedJobs) {
        allItems.push(...savedJobs.map(j => ({ id: j.id, created_at: j.saved_at, type: 'saved_job' as const, data: j as any })));
      }

      // Sort all by date
      allItems.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      setHistoryItems(allItems);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Failed to load history",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async (item: HistoryItem) => {
    try {
      const tableMap: Record<HistoryItem['type'], string> = {
        document: 'documents',
        resume: 'resume_versions',
        letter: 'letters',
        ats: 'ats_scores',
        interview: 'interview_sessions',
        job_analysis: 'job_analyses',
        salary: 'salary_analyses',
        saved_job: 'saved_jobs',
      };

      const table = tableMap[item.type];
      const { error } = await supabase.from(table as any).delete().eq('id', item.id);

      if (error) throw error;

      // Handle storage deletion for documents
      if (item.type === 'document' && (item.data as Document).storage_path) {
        await supabase.storage
          .from('documents')
          .remove([(item.data as Document).storage_path]);
      }

      toast({
        title: "Item deleted",
        description: "The item has been removed from your history.",
      });

      loadAllHistory();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Delete failed",
        description: error.message,
      });
    }
  };

  const viewItem = (item: HistoryItem) => {
    switch (item.type) {
      case 'interview':
        navigate(`/interview/results/${item.id}`);
        break;
      case 'job_analysis':
        navigate(`/job-analyzer`);
        break;
      case 'ats':
        navigate(`/ats-optimization`);
        break;
      case 'salary':
        navigate(`/salary-negotiation`);
        break;
      case 'saved_job':
        const job = item.data as SavedJob;
        if (job.url) window.open(job.url, '_blank');
        break;
    }
  };

  const filteredItems = historyItems.filter(item => {
    // Filter by tab
    if (activeTab !== 'all' && item.type !== activeTab) return false;

    // Filter by search
    const query = searchQuery.toLowerCase();
    switch (item.type) {
      case 'document':
        return (item.data as Document).title.toLowerCase().includes(query);
      case 'resume':
        return true;
      case 'letter':
        const letter = item.data as Letter;
        return letter.job_title.toLowerCase().includes(query) || 
               letter.company.toLowerCase().includes(query);
      case 'ats':
        return true;
      case 'interview':
        const interview = item.data as InterviewSession;
        return (interview.job_title?.toLowerCase().includes(query) || 
                interview.company_name?.toLowerCase().includes(query));
      case 'job_analysis':
        const analysis = item.data as JobAnalysis;
        return analysis.job_title.toLowerCase().includes(query) || 
               analysis.company?.toLowerCase().includes(query);
      case 'salary':
        const salary = item.data as SalaryAnalysis;
        return salary.job_title.toLowerCase().includes(query) || 
               salary.location.toLowerCase().includes(query);
      case 'saved_job':
        const job = item.data as SavedJob;
        return job.title.toLowerCase().includes(query) || 
               job.company.toLowerCase().includes(query);
      default:
        return true;
    }
  });

  const getItemIcon = (type: HistoryItem['type']) => {
    switch (type) {
      case 'document':
      case 'resume':
      case 'letter':
        return <FileText className="h-4 w-4" />;
      case 'ats':
        return <Target className="h-4 w-4" />;
      case 'interview':
        return <Briefcase className="h-4 w-4" />;
      case 'job_analysis':
        return <Search className="h-4 w-4" />;
      case 'salary':
        return <DollarSign className="h-4 w-4" />;
      case 'saved_job':
        return <Building2 className="h-4 w-4" />;
    }
  };

  const getItemTitle = (item: HistoryItem) => {
    switch (item.type) {
      case 'document':
        return (item.data as Document).title;
      case 'resume':
        return 'Resume Version';
      case 'letter':
        const letter = item.data as Letter;
        return `${letter.job_title} at ${letter.company}`;
      case 'ats':
        const ats = item.data as ATSScore;
        return `ATS Score: ${ats.overall_score}%`;
      case 'interview':
        const interview = item.data as InterviewSession;
        return `${interview.interview_type} Interview${interview.job_title ? ` - ${interview.job_title}` : ''}`;
      case 'job_analysis':
        const analysis = item.data as JobAnalysis;
        return `Job Analysis: ${analysis.job_title}`;
      case 'salary':
        const salary = item.data as SalaryAnalysis;
        return `Salary Analysis: ${salary.job_title}`;
      case 'saved_job':
        const job = item.data as SavedJob;
        return job.title;
    }
  };

  const getItemSubtitle = (item: HistoryItem) => {
    switch (item.type) {
      case 'letter':
        return (item.data as Letter).tone;
      case 'interview':
        return (item.data as InterviewSession).status;
      case 'job_analysis':
        return (item.data as JobAnalysis).company || 'No company';
      case 'salary':
        return (item.data as SalaryAnalysis).location;
      case 'saved_job':
        return (item.data as SavedJob).company;
      default:
        return null;
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Document & Activity History</h1>
          <p className="text-muted-foreground mt-2">View and manage all your documents, analyses, and saved items</p>
        </div>

        <Card className="p-6">
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search your history..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="flex flex-wrap justify-start gap-2 h-auto mb-6 bg-transparent p-0">
              <TabsTrigger value="all" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">All</TabsTrigger>
              <TabsTrigger value="document" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Documents</TabsTrigger>
              <TabsTrigger value="resume" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Resumes</TabsTrigger>
              <TabsTrigger value="letter" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Letters</TabsTrigger>
              <TabsTrigger value="ats" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">ATS Scores</TabsTrigger>
              <TabsTrigger value="interview" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Interviews</TabsTrigger>
              <TabsTrigger value="job_analysis" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Job Analysis</TabsTrigger>
              <TabsTrigger value="saved_job" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Saved Jobs</TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab}>
              {loading ? (
                <div className="text-center py-12 text-muted-foreground">
                  Loading your history...
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    {searchQuery ? 'No items match your search' : 'No history yet'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Your documents and activity will appear here
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[140px]">Type</TableHead>
                        <TableHead className="min-w-[200px]">Title</TableHead>
                        <TableHead className="w-[200px]">Details</TableHead>
                        <TableHead className="w-[180px]">Date</TableHead>
                        <TableHead className="text-right w-[120px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                  <TableBody>
                    {filteredItems.map((item) => (
                      <TableRow key={`${item.type}-${item.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getItemIcon(item.type)}
                            <Badge variant="outline" className="capitalize">
                              {item.type.replace('_', ' ')}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="truncate max-w-[200px]">{getItemTitle(item)}</div>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          <div className="truncate max-w-[180px]">{getItemSubtitle(item)}</div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {format(new Date(item.created_at), 'PPp')}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {['interview', 'job_analysis', 'ats', 'salary', 'saved_job'].includes(item.type) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => viewItem(item)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteItem(item)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </DashboardLayout>
  );
}
